import { Text } from "@react-three/drei";
import type { ComponentProps } from "react";
import { FONT, fontOrDefault } from "./fonts";
import type { FontUrl } from "./fonts";
import { INK, RO, Z } from "../theme3d";
import { usePanelOccluded } from "./PanelOcclusionContext";

export type Tier =
  | "kicker"
  | "title"
  | "titleAccent"
  | "seiji"
  | "instruction"
  | "sectionLabel"
  | "hint"
  | "action"
  | "actionSub"
  | "buttonLabel"
  | "chip"
  | "secondary"
  | "footerHint"
  | "feedbackOk"
  | "feedbackWarn"
  | "countdownKanji";

type TextProps = ComponentProps<typeof Text>;

interface TierStyle {
  font: FontUrl;
  fontSize: number;
  color: string;
  fillOpacity?: number;
  letterSpacing: number;
  lineHeight?: number;
}

// Sizes are in meters, tuned for legibility at the panels' real in-headset
// viewing distance (~1.9m), not scaled down from the flat mockup's pixel
// instinct — see HANDOFF-main-menu-visual-redesign.md §3. `fillOpacity`
// stands in for the mockup's CSS opacity modifiers (troika's <Text> has no
// separate alpha channel on `color`, but does expose fillOpacity).
const TIERS: Record<Tier, TierStyle> = {
  kicker: { font: FONT.gothicMedium, fontSize: 0.03, color: INK.body, fillOpacity: 0.62, letterSpacing: 0.3 },
  title: { font: FONT.minchoBlack, fontSize: 0.095, color: INK.title, letterSpacing: 0.12 },
  titleAccent: { font: FONT.minchoBold, fontSize: 0.032, color: INK.accent, fillOpacity: 0.7, letterSpacing: 0.3 },
  seiji: { font: FONT.minchoBold, fontSize: 0.046, color: INK.seiji, letterSpacing: 0, lineHeight: 1.5 },
  instruction: { font: FONT.gothicMedium, fontSize: 0.038, color: INK.body, letterSpacing: 0, lineHeight: 1.4 },
  sectionLabel: { font: FONT.gothicBold, fontSize: 0.03, color: INK.accent, letterSpacing: 0.3 },
  hint: { font: FONT.gothicMedium, fontSize: 0.032, color: INK.accent, letterSpacing: 0, lineHeight: 1.4 },
  action: { font: FONT.gothicBold, fontSize: 0.062, color: INK.title, letterSpacing: 0.1 },
  actionSub: { font: FONT.gothicMedium, fontSize: 0.046, color: INK.body, fillOpacity: 0.8, letterSpacing: 0.08 },
  buttonLabel: { font: FONT.gothicBold, fontSize: 0.044, color: INK.paper, letterSpacing: 0.06 },
  chip: { font: FONT.gothicBold, fontSize: 0.026, color: INK.body, letterSpacing: 0.04 },
  secondary: { font: FONT.gothicMedium, fontSize: 0.034, color: INK.body, fillOpacity: 0.55, letterSpacing: 0.06 },
  footerHint: { font: FONT.gothicMedium, fontSize: 0.028, color: INK.body, fillOpacity: 0.42, letterSpacing: 0.12 },
  feedbackOk: { font: FONT.gothicMedium, fontSize: 0.036, color: INK.title, letterSpacing: 0 },
  feedbackWarn: { font: FONT.gothicMedium, fontSize: 0.036, color: INK.warn, letterSpacing: 0 },
  countdownKanji: { font: FONT.minchoBlack, fontSize: 0.55, color: INK.title, letterSpacing: 0 },
};

export interface PanelTextProps extends Omit<TextProps, "font" | "characters" | "position"> {
  tier: Tier;
  // Defaults to the enclosing ShojiBacking's `occluded` (via context) — no
  // need to repeat it on every line of a panel. Pass explicitly only to
  // override a single line against its panel's convention.
  occluded?: boolean;
  position?: [number, number, number];
}

// Every <Text> in the panel system goes through this — never <Text>
// directly — so tier styles, font wiring, and the occlusion convention stay
// consistent. Deliberately never forwards `characters`: the cache key
// fonts.ts warms is ['troika-text', font, undefined], and passing
// `characters` here would silently miss that cache and risk a real Suspense
// throw (see fonts.ts's module doc).
export function PanelText({
  tier,
  occluded,
  position = [0, 0, 0],
  children,
  ...props
}: PanelTextProps) {
  const style = TIERS[tier];
  const resolvedOccluded = usePanelOccluded(occluded);
  const [x, y, z] = position;

  return (
    <Text
      font={fontOrDefault(style.font)}
      fontSize={style.fontSize}
      color={style.color}
      fillOpacity={style.fillOpacity}
      letterSpacing={style.letterSpacing}
      lineHeight={style.lineHeight}
      anchorX="center"
      anchorY="middle"
      position={[x, y, z + Z.text]}
      renderOrder={resolvedOccluded ? RO.panelText : RO.overlayText}
      material-depthTest={resolvedOccluded}
      {...props}
    >
      {children}
    </Text>
  );
}
